export class CurrencyFormatterUtil {
  /**
   * Formats currency amount with K, Lakh, Crore abbreviations
   * @param amount - The amount to format
   * @param decimalPlaces - Number of decimal places (default: 2)
   * @param locale - Currency locale (default: 'INR')
   * @returns Formatted currency string
   */
  static formatCurrency(
    amount: number | null | undefined,
    decimalPlaces: number = 2,
    locale: string = 'INR'
  ): string {
    try {
      // Handle null, undefined, or invalid values
      if (amount === null || amount === undefined || isNaN(amount)) {
        return '₹0';
      }

      // Convert to absolute value for formatting
      const absAmount = Math.abs(amount);
      const isNegative = amount < 0;
      const currencySymbol = locale === 'INR' ? '₹' : '$';

      let formattedValue: string;
      let suffix: string;

      if (absAmount >= 10000000) {
        // 1 Crore and above
        formattedValue = (absAmount / 10000000).toFixed(decimalPlaces);
        suffix = ' Cr';
      } else if (absAmount >= 100000) {
        // 1 Lakh and above
        formattedValue = (absAmount / 100000).toFixed(decimalPlaces);
        suffix = ' L';
      } else if (absAmount >= 1000) {
        // 1 Thousand and above
        formattedValue = (absAmount / 1000).toFixed(decimalPlaces);
        suffix = 'K';
      } else {
        // Less than 1000
        formattedValue = absAmount.toFixed(decimalPlaces);
        suffix = '';
      }

      // Remove unnecessary trailing zeros after decimal
      formattedValue = parseFloat(formattedValue).toString();

      // Add negative sign if needed
      const sign = isNegative ? '-' : '';

      return `${sign}${currencySymbol}${formattedValue}${suffix}`;
    } catch (error) {
      console.error('Error formatting currency:', error);
      return '₹0';
    }
  }

  /**
   * Formats currency with Indian numbering system (with commas)
   * @param amount - The amount to format
   * @param includeDecimals - Whether to include decimal places
   * @returns Formatted currency string
   */
  static formatIndianCurrency(
    amount: number | null | undefined,
    includeDecimals: boolean = true
  ): string {
    try {
      if (amount === null || amount === undefined || isNaN(amount)) {
        return '₹0';
      }

      const isNegative = amount < 0;
      const absAmount = Math.abs(amount);
      const sign = isNegative ? '-' : '';

      // Format with Indian numbering system
      let formatted = absAmount.toLocaleString('en-IN', {
        minimumFractionDigits: includeDecimals ? 2 : 0,
        maximumFractionDigits: includeDecimals ? 2 : 0,
      });

      return `${sign}₹${formatted}`;
    } catch (error) {
      console.error('Error formatting Indian currency:', error);
      return '₹0';
    }
  }

  /**
   * Converts abbreviated currency back to full number
   * @param value - The abbreviated value (e.g., "1.5K", "2.3L")
   * @returns The full numeric value
   */
  static parseAbbreviatedCurrency(value: string): number {
    try {
      if (!value || typeof value !== 'string') {
        return 0;
      }

      // Remove currency symbol and spaces
      const cleaned = value.replace(/[₹$,\s]/g, '').trim();
      
      // Extract number and suffix
      const match = cleaned.match(/^(-?[\d.]+)([KLCr]*)$/i);
      
      if (!match) {
        return parseFloat(cleaned) || 0;
      }

      const number = parseFloat(match[1]);
      const suffix = match[2].toUpperCase();

      let multiplier = 1;
      if (suffix === 'K') {
        multiplier = 1000;
      } else if (suffix === 'L') {
        multiplier = 100000;
      } else if (suffix === 'CR') {
        multiplier = 10000000;
      }

      return number * multiplier;
    } catch (error) {
      console.error('Error parsing abbreviated currency:', error);
      return 0;
    }
  }
}